import { Text } from '@scality/core-ui';
import type React from 'react';
import { Navigate, Outlet } from 'react-router';
import { CenteredContent, CenteredLoader } from '../../components/CenteredContent';
import { AppSidebar } from '../../components/sidebar/AppSidebar';
import { useIsInternalUser } from '../../hooks/useIsInternalUser';
import { useIsNarrowViewport } from '../../hooks/useIsNarrowViewport';
import { AppShell } from '../../routes/AppShell';
import { CONVERSATION_PATH } from '../../routes/paths';
import { DashboardList } from '../components/DashboardList';
import { useDashboardTemplates } from '../hooks/useDashboards';

/**
 * Dashboard mode layout: the dashboard-list sidebar + the routed dashboard view.
 * The split (dashboard + chat) needs room, so on a narrow viewport we bounce the whole
 * `/dashboards/*` subtree back to the conversation. The templates summary is fetched here (one level
 * above the routes) so every entry — index redirect or deep link to a specific
 * dashboard — shares a single loader and a loaded template list to validate against.
 */
export const DashboardLayout: React.FC = () => {
  const isNarrow = useIsNarrowViewport();
  const { isInternal, isLoading: isInternalLoading } = useIsInternalUser();
  const { isLoading, isError } = useDashboardTemplates();

  if (isNarrow) {
    return <Navigate to={CONVERSATION_PATH} replace />;
  }

  // The internal check can await /health, so hold the redirect until it settles —
  // bouncing early would break deep links into a dashboard on demo envs.
  if (isInternalLoading) {
    return <CenteredLoader offsetHeader />;
  }

  if (!isInternal) {
    return <Navigate to={CONVERSATION_PATH} replace />;
  }

  return (
    <AppShell sidebar={<AppSidebar body={<DashboardList />} />}>
      {isLoading ? (
        <CenteredLoader offsetHeader />
      ) : isError ? (
        <CenteredContent $offsetHeader>
          <Text color="statusCritical">Failed to load dashboards.</Text>
        </CenteredContent>
      ) : (
        <Outlet />
      )}
    </AppShell>
  );
};
